/**
 * Cyberpunk Color System
 * Provides theme-aware colors for both dark and light cyberpunk modes
 */

// Dark Cyberpunk Colors (neon on dark)
export const darkCyber = {
  // Backgrounds
  bgPrimary: '#0f172a',
  bgSecondary: '#1e293b',
  bgCard: 'rgba(15, 23, 42, 0.95)',
  bgCardHover: 'rgba(30, 41, 59, 0.95)',
  bgOverlay: 'rgba(15, 23, 42, 0.8)',
  bgGlass: 'rgba(15, 23, 42, 0.6)',
  
  // Gradients
  gradientBg: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
  gradientCard: 'linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.98) 100%)',
  gradientGlow: 'radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.08) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.06) 0%, transparent 50%)',
  gradientFab: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(37, 99, 235, 0.2) 100%)',
  gradientFabInactive: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(51, 65, 85, 0.8) 100%)',
  
  // Text
  textPrimary: '#f8fafc',
  textSecondary: 'rgba(248, 250, 252, 0.7)',
  textMuted: '#94a3b8',
  
  // Neon accents
  neonBlue: '#60a5fa',
  neonCyan: '#06b6d4',
  neonPurple: '#a855f7',
  neonPink: '#ec4899',
  neonGreen: '#22c55e',
  neonYellow: '#fbbf24',
  neonGold: '#fbbf24',  // Alias for neonYellow
  neonOrange: '#f59e0b',
  neonRed: '#ef4444',
  
  // Chip colors for dark mode - bright and visible
  chipBlue: '#93c5fd',   // blue-300
  chipGreen: '#86efac',  // green-300
  chipYellow: '#fde047', // yellow-300
  chipCyan: '#67e8f9',   // cyan-300
  
  // Borders
  borderDefault: 'rgba(96, 165, 250, 0.2)',
  borderHover: 'rgba(96, 165, 250, 0.4)',
  borderActive: 'rgba(96, 165, 250, 0.6)',
  borderBadge: 'rgba(15, 23, 42, 0.9)',
  
  // Shadows/Glows
  glowBlue: '0 0 20px rgba(96, 165, 250, 0.4)',
  glowCyan: '0 0 20px rgba(6, 182, 212, 0.4)',
  glowPurple: '0 0 20px rgba(168, 85, 247, 0.4)',
  shadowCard: '0 8px 32px rgba(0, 0, 0, 0.4)',
  shadowElevated: '0 12px 48px rgba(0, 0, 0, 0.5), 0 0 20px rgba(96, 165, 250, 0.15)',
  shadowFab: '0 8px 32px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
};

// Light Cyberpunk Colors (neon on white/light gray)
export const lightCyber = {
  // Backgrounds - clean whites with subtle blue tint
  bgPrimary: '#f0f4ff',
  bgSecondary: '#ffffff',
  bgCard: 'rgba(255, 255, 255, 0.95)',
  bgCardHover: 'rgba(240, 244, 255, 0.98)',
  bgOverlay: 'rgba(255, 255, 255, 0.9)',
  bgGlass: 'rgba(255, 255, 255, 0.7)',
  
  // Gradients - subtle, elegant
  gradientBg: 'linear-gradient(180deg, #ffffff 0%, #f0f4ff 100%)',
  gradientCard: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(240, 244, 255, 0.98) 100%)',
  gradientGlow: 'radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.06) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.04) 0%, transparent 50%)',
  gradientFab: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.15) 100%)',
  gradientFabInactive: 'linear-gradient(135deg, rgba(241, 245, 249, 0.95) 0%, rgba(226, 232, 240, 0.95) 100%)',
  
  // Text - dark for readability (IMPROVED)
  textPrimary: '#0f172a',  // Darker for better contrast
  textSecondary: '#334155', // Solid color instead of transparent
  textMuted: '#475569',     // Darker muted for readability
  
  // Neon accents - DARKER for better contrast on white
  neonBlue: '#1d4ed8',    // blue-700
  neonCyan: '#0e7490',    // cyan-700
  neonPurple: '#7c3aed',  // violet-600
  neonPink: '#be185d',    // pink-700
  neonGreen: '#047857',   // emerald-700
  neonYellow: '#a16207',  // yellow-700
  neonGold: '#a16207',
  neonOrange: '#c2410c',  // orange-700
  neonRed: '#b91c1c',     // red-700
  
  // Chip colors for light mode - darker for readability
  chipBlue: '#1e40af',    // blue-800
  chipGreen: '#065f46',   // emerald-800
  chipYellow: '#854d0e',  // yellow-800
  chipCyan: '#155e75',    // cyan-800
  
  // Borders - more visible on white
  borderDefault: 'rgba(59, 130, 246, 0.25)',
  borderHover: 'rgba(59, 130, 246, 0.5)',
  borderActive: 'rgba(59, 130, 246, 0.7)',
  borderBadge: 'rgba(255, 255, 255, 0.9)',
  
  // Shadows - softer, more elegant
  glowBlue: '0 0 15px rgba(59, 130, 246, 0.2)',
  glowCyan: '0 0 15px rgba(8, 145, 178, 0.2)',
  glowPurple: '0 0 15px rgba(147, 51, 234, 0.2)',
  shadowCard: '0 4px 20px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(59, 130, 246, 0.1)',
  shadowElevated: '0 8px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(59, 130, 246, 0.15)',
  shadowFab: '0 4px 20px rgba(59, 130, 246, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
};

// High Contrast Dark Mode - Maximum visibility for outdoor/desert use
export const highContrastDark = {
  // Backgrounds - Pure black for maximum contrast
  bgPrimary: '#000000',
  bgSecondary: '#0a0a0a',
  bgCard: '#0a0a0a',
  bgCardHover: '#1a1a1a',
  bgOverlay: 'rgba(0, 0, 0, 0.95)',
  bgGlass: 'rgba(0, 0, 0, 0.85)',
  
  // Gradients - Solid dark
  gradientBg: 'linear-gradient(180deg, #000000 0%, #0a0a0a 100%)',
  gradientCard: '#0a0a0a',
  gradientGlow: 'none',
  gradientFab: 'linear-gradient(135deg, rgba(255, 255, 0, 0.3) 0%, rgba(255, 200, 0, 0.3) 100%)',
  gradientFabInactive: '#1a1a1a',
  
  // Text - Maximum brightness
  textPrimary: '#ffffff',
  textSecondary: '#e5e5e5',
  textMuted: '#d4d4d4',
  
  // Neon accents - BRIGHT and saturated
  neonBlue: '#60a5fa',
  neonCyan: '#22d3ee',
  neonPurple: '#c084fc',
  neonPink: '#f472b6',
  neonGreen: '#4ade80',
  neonYellow: '#fde047',
  neonGold: '#fde047',
  neonOrange: '#fb923c',
  neonRed: '#f87171',
  
  // Chip colors - bright
  chipBlue: '#93c5fd',
  chipGreen: '#86efac',
  chipYellow: '#fef08a',
  chipCyan: '#67e8f9',
  
  // Borders - Very visible
  borderDefault: 'rgba(255, 255, 255, 0.3)',
  borderHover: 'rgba(255, 255, 255, 0.5)',
  borderActive: 'rgba(255, 255, 255, 0.7)',
  borderBadge: '#000000',
  
  // Shadows - High contrast glows
  glowBlue: '0 0 20px rgba(96, 165, 250, 0.6)',
  glowCyan: '0 0 20px rgba(34, 211, 238, 0.6)',
  glowPurple: '0 0 20px rgba(192, 132, 252, 0.6)',
  shadowCard: '0 0 0 2px rgba(255, 255, 255, 0.2)',
  shadowElevated: '0 0 0 2px rgba(255, 255, 255, 0.3), 0 8px 32px rgba(0, 0, 0, 0.8)',
  shadowFab: '0 0 20px rgba(253, 224, 71, 0.5)',
};

// High Contrast Light Mode - Maximum visibility for bright sunlight
export const highContrastLight = {
  // Backgrounds - Pure white
  bgPrimary: '#ffffff',
  bgSecondary: '#ffffff',
  bgCard: '#ffffff',
  bgCardHover: '#f5f5f5',
  bgOverlay: 'rgba(255, 255, 255, 0.98)',
  bgGlass: 'rgba(255, 255, 255, 0.95)',
  
  // Gradients - Solid white
  gradientBg: '#ffffff',
  gradientCard: '#ffffff',
  gradientGlow: 'none',
  gradientFab: 'linear-gradient(135deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.15) 100%)',
  gradientFabInactive: '#f5f5f5',
  
  // Text - Pure black for maximum contrast
  textPrimary: '#000000',
  textSecondary: '#171717',
  textMuted: '#262626',
  
  // Accents - Very dark, high contrast
  neonBlue: '#1e3a8a',    // blue-900
  neonCyan: '#164e63',    // cyan-900
  neonPurple: '#581c87',  // purple-900
  neonPink: '#831843',    // pink-900
  neonGreen: '#14532d',   // green-900
  neonYellow: '#713f12',  // yellow-900
  neonGold: '#713f12',
  neonOrange: '#7c2d12',  // orange-900
  neonRed: '#7f1d1d',     // red-900
  
  // Chip colors - Very dark
  chipBlue: '#1e3a8a',
  chipGreen: '#14532d',
  chipYellow: '#713f12',
  chipCyan: '#164e63',
  
  // Borders - Very dark and visible
  borderDefault: 'rgba(0, 0, 0, 0.3)',
  borderHover: 'rgba(0, 0, 0, 0.5)',
  borderActive: 'rgba(0, 0, 0, 0.7)',
  borderBadge: '#ffffff',
  
  // Shadows - Strong definition
  glowBlue: '0 0 0 3px rgba(30, 58, 138, 0.3)',
  glowCyan: '0 0 0 3px rgba(22, 78, 99, 0.3)',
  glowPurple: '0 0 0 3px rgba(88, 28, 135, 0.3)',
  shadowCard: '0 0 0 2px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
  shadowElevated: '0 0 0 2px rgba(0, 0, 0, 0.2), 0 8px 24px rgba(0, 0, 0, 0.15)',
  shadowFab: '0 0 0 2px rgba(0, 0, 0, 0.2), 0 4px 12px rgba(0, 0, 0, 0.15)',
};

// Helper function to get theme colors based on mode and contrast
export const getCyberColors = (darkMode: boolean, highContrast: boolean = false) => {
  if (highContrast) {
    return darkMode ? highContrastDark : highContrastLight;
  }
  return darkMode ? darkCyber : lightCyber;
};

// Type for theme colors
export type CyberColors = typeof darkCyber;
